import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLanguage } from "@/i18n";
import { GUIDE_IMAGE } from "@/tours";
import { Reveal, MaskedLine } from "@/components/Reveal";

export default function Manifesto() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <section id="guida" ref={ref} data-testid="manifesto-section" className="relative scroll-mt-24 bg-azure py-24 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[#0B334D] via-transparent to-[#0B334D]/60" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14 lg:gap-10">
        <div className="lg:col-span-5 relative">
          <Reveal className="relative z-10">
            <div className="overflow-hidden rounded-t-full rounded-b-md aspect-[3/4] max-w-md">
              <motion.img
                src={GUIDE_IMAGE}
                alt="Guido, guida locale, affacciato sulla costa"
                data-testid="manifesto-guide-image"
                style={{ y: imgY }}
                className="w-full h-[112%] object-cover"
              />
            </div>
          </Reveal>
          <Reveal delay={0.2} className="absolute -bottom-14 -right-2 lg:right-4 z-20 hidden sm:block">
            <div className="w-64 lg:w-80 overflow-hidden rounded-md rotate-3 border-8 border-[#FDFBF7] shadow-xl">
              <img src="/images/plebiscito.webp" alt="Piazza del Plebiscito, Napoli" className="w-full aspect-[3/4] object-cover" />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7 lg:pl-10">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-dune mb-5">{t.manifesto.overline}</p>
          </Reveal>
          <h2 className="font-serif font-light tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-[#FDFBF7] mb-14 flex flex-wrap items-baseline gap-x-4">
            <MaskedLine delay={0.1}>{t.manifesto.title1}</MaskedLine>
            <MaskedLine delay={0.22}>
              <span className="italic text-dune">{t.manifesto.title2}</span>
            </MaskedLine>
          </h2>

          <div className="space-y-0">
            {t.manifesto.chapters.map((ch, i) => (
              <Reveal key={ch.n} delay={0.1 + i * 0.1}>
                <div data-testid={`manifesto-chapter-${ch.n}`} className="grid grid-cols-[auto_1fr] gap-6 lg:gap-10 py-8 border-t border-[#F3EFE9]/15">
                  <span className="font-serif italic text-3xl lg:text-4xl text-[#C9A227] leading-none pt-1">{ch.n}</span>
                  <div>
                    <h3 className="text-xl lg:text-2xl font-semibold tracking-tight text-[#FDFBF7] mb-3">{ch.title}</h3>
                    <p className="text-base leading-relaxed text-[#F3EFE9]/75 max-w-xl">{ch.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
